import { ComponentProps, ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatRelativeTimeFromNow } from 'pages/ai-chat/utils/relativeTime';
import { GLOBAL_SEARCH_MIN_QUERY_LENGTH } from 'types/globalSearch';
import { cn } from 'utils/cn';
import { pathForResult, searchResultsPath } from 'utils/globalSearchRoutes';
import { GlobalSearchBestBetRow } from './GlobalSearchBestBetRow';
import { GlobalSearchRow, isPlainLeftClick } from './GlobalSearchRow';
import { GlobalSearchSection } from './GlobalSearchSection';
import { GlobalSearchSkeleton } from './GlobalSearchSkeleton';
import { HighlightedText } from './HighlightedText';
import { RowWithPeek } from './RowWithPeek';
import {
    GLOBAL_SEARCH_ENTITY_ICON,
    GLOBAL_SEARCH_ENTITY_LABEL_ID,
    GLOBAL_SEARCH_PAGE_ICON,
    secondaryLineClass,
} from './entityDisplay';
import { GlobalSearchOptionsModel } from './useGlobalSearchOptions';

/**
 * Which surface the panel is drawn on.
 *
 * `dropdown` hangs it under the header's box or the dialog's field; `sheet` fills the narrow-width
 * sheet, which owns the whole viewport. The list itself is the same either way — what differs is
 * where it is bounded, where the peek card can go, and that the sheet's footer is pinned to the
 * bottom of the screen rather than sitting under the last row.
 */
export type GlobalSearchPanelVariant = 'dropdown' | 'sheet';

/** Where the peek card goes on each surface: beside a dropdown row, over the sheet's own width. */
const PEEK_PLACEMENT: Record<GlobalSearchPanelVariant, Pick<ComponentProps<typeof RowWithPeek>, 'side' | 'align'>> = {
    dropdown: {},
    // The sheet's rows span it, so a card centred on a row is a card centred on the sheet.
    sheet: { side: 'bottom', align: 'center' },
};

export interface GlobalSearchDropdownProps {
    /** Everything there is to list, built beside the field — see `useGlobalSearchOptions`. */
    model: GlobalSearchOptionsModel;
    /** The listbox's id, which the search box points `aria-controls` at. */
    listboxId: string;
    /**
     * The row the selection is on. Only the keys move it: the pointer resting on a row never does,
     * so a list that grows or reflows under a still pointer cannot take the selection away from the
     * row Enter is about to open.
     */
    activeOptionId?: string;
    variant?: GlobalSearchPanelVariant;
    /**
     * Take the better spelling the backend offered. The correction is drawn only where there is
     * somewhere to put the term back — the field the surface owns — so a caller that has no way to
     * rewrite the query leaves this out and the panel offers no correction.
     */
    onDidYouMean?: (term: string) => void;
    /**
     * Sections a surface adds under the results, inside the same listbox — the dialog's actions on
     * the top hit and its hand-off to Crespect AI — held below the scrolling results, so they stay
     * on screen. Drawn with results, and under the message of a search that failed or found nothing;
     * the surface puts their options after the model's own when it asks for the keys, and passes
     * nothing when it has none.
     */
    children?: ReactNode;
    /** The surface draws its Ask Crespect AI row under a failed or empty search, so the message points at it. */
    offersAskAi?: boolean;
    /** Drawn under the results in place of the "see all" band — the dialog's key hints and its own link. */
    footer?: ReactNode;
}

/**
 * The typeahead panel — under the search box at full width, and filling the sheet below the
 * breakpoint. One list, two frames: see {@link GlobalSearchPanelVariant}.
 *
 * It draws what it is handed and decides nothing about what is in the list: the model is built
 * beside the search field, because the field is what has to name the selected option to a screen
 * reader. Pages and actions are merged into that model on this side — they are routes, with no row
 * in any tenant schema, so the backend is never told about them.
 */
export function GlobalSearchDropdown({
    model,
    listboxId,
    activeOptionId,
    variant = 'dropdown',
    onDidYouMean,
    children,
    offersAskAi = false,
    footer,
}: GlobalSearchDropdownProps) {
    const sheet = variant === 'sheet';
    const surface = sheet ? 'sheet' : 'dropdown';
    const intl = useIntl();
    // The record row the pointer is on, which is the one row allowed a peek card. Kept apart from
    // the selection, which belongs to the keys.
    const [hoveredId, setHoveredId] = useState<string>();
    const {
        trimmed,
        data,
        isLoading,
        isError,
        isTimedOut,
        showRecents,
        recentRows,
        bestBet,
        pageRows,
        sectionRows,
        options,
    } = model;

    const showSkeleton = !showRecents && isLoading;
    const hasOptions = options.length > 0;
    const minLengthHint = intl.formatMessage(
        { id: 'global-search.min-length-hint' },
        { count: GLOBAL_SEARCH_MIN_QUERY_LENGTH }
    );

    const pageSection = pageRows.length > 0 && (
        <GlobalSearchSection
            title={intl.formatMessage({ id: 'global-search.pages-and-actions' })}
            count={pageRows.length}
        >
            {pageRows.map(({ id, route, select }) => (
                <GlobalSearchRow
                    key={id}
                    id={id}
                    icon={GLOBAL_SEARCH_PAGE_ICON}
                    title={route.label}
                    secondary={
                        <>
                            {intl.formatMessage({
                                id: route.path.endsWith('/new') ? 'global-search.action' : 'global-search.page',
                            })}
                            {' · '}
                            <span className="font-mono">{route.path}</span>
                        </>
                    }
                    href={route.path}
                    selected={id === activeOptionId}
                    onSelect={select}
                />
            ))}
        </GlobalSearchSection>
    );
    // Whatever is drawn under the pages is set off from them by the same hairline a section gets.
    const underPages = pageRows.length > 0;
    // One sentence each for a failed search and an empty one, shown and said to assistive tech alike. A search
    // that timed out failed on what was typed, not on the service, so it says how to narrow the query.
    const outageId = offersAskAi ? 'global-search.error-ask-ai' : 'global-search.error';
    const errorText = intl.formatMessage({ id: isTimedOut ? 'global-search.timed-out' : outageId });
    const nothingFoundText = intl.formatMessage(
        { id: offersAskAi ? 'global-search.nothing-found-ask-ai' : 'global-search.nothing-found' },
        { query: trimmed }
    );
    const errorMessage = <Message text={errorText} />;

    let body: ReactNode = null;
    if (!hasOptions) {
        if (showSkeleton) {
            body = <GlobalSearchSkeleton />;
        } else if (isError || !showRecents) {
            body = (
                <>
                    {isError ? errorMessage : <Message text={nothingFoundText} />}
                    {children ? (
                        <div
                            id={listboxId}
                            role="listbox"
                            aria-label={intl.formatMessage({ id: 'global-search.results-label' })}
                        >
                            {children}
                        </div>
                    ) : null}
                </>
            );
        }
    } else {
        // The surface's own sections stay under the results instead of scrolling away below them.
        const extras = showRecents ? undefined : children;
        let records: ReactNode;
        if (showRecents) {
            records = recentRows.length > 0 && (
                <GlobalSearchSection
                    title={intl.formatMessage({ id: 'global-search.recently-viewed' })}
                    divided={underPages}
                    action={{
                        label: intl.formatMessage({ id: 'clear' }),
                        onSelect: model.clearRecents,
                    }}
                >
                    {recentRows.map(({ id, record, select }) => (
                        <RowWithPeek
                            key={id}
                            entityType={record.entityType}
                            recordId={record.id}
                            surface={surface}
                            hovered={id === hoveredId}
                            {...PEEK_PLACEMENT[variant]}
                            row={
                                <GlobalSearchRow
                                    id={id}
                                    icon={GLOBAL_SEARCH_ENTITY_ICON[record.entityType]}
                                    entityType={record.entityType}
                                    title={record.displayName}
                                    secondary={intl.formatMessage({
                                        id: GLOBAL_SEARCH_ENTITY_LABEL_ID[record.entityType],
                                    })}
                                    trailing={formatRelativeTimeFromNow(intl, record.viewedAt)}
                                    href={pathForResult(record.entityType, record.id)}
                                    selected={id === activeOptionId}
                                    onHover={() => setHoveredId(id)}
                                    onSelect={select}
                                />
                            }
                        />
                    ))}
                </GlobalSearchSection>
            );
        } else if (showSkeleton || isError) {
            // The pages are already drawn; only the records are still on their way, or failed to come.
            records = (
                <div className={cn(underPages && 'border-grey-200 border-t')}>
                    {showSkeleton ? <GlobalSearchSkeleton /> : errorMessage}
                </div>
            );
        } else {
            records = sectionRows.map(({ section, rows }, index) => (
                <GlobalSearchSection
                    key={section.entityType}
                    title={intl.formatMessage({ id: GLOBAL_SEARCH_ENTITY_LABEL_ID[section.entityType] })}
                    count={section.count}
                    divided={index > 0 || underPages}
                    action={{
                        label: intl.formatMessage({ id: 'global-search.see-all' }),
                        href: searchResultsPath(trimmed, section.entityType),
                        onSelect: () => model.openResultsPage(section.entityType),
                    }}
                >
                    {rows.map(({ id, row, select }) => (
                        <RowWithPeek
                            key={id}
                            entityType={row.entityType}
                            recordId={row.id}
                            surface={surface}
                            hovered={id === hoveredId}
                            {...PEEK_PLACEMENT[variant]}
                            row={
                                <GlobalSearchRow
                                    id={id}
                                    icon={GLOBAL_SEARCH_ENTITY_ICON[row.entityType]}
                                    entityType={row.entityType}
                                    title={row.displayName}
                                    draft={row.isDraft}
                                    secondary={<HighlightedText text={row.secondaryLine} />}
                                    secondaryClassName={secondaryLineClass(row.secondaryLineKind)}
                                    href={pathForResult(row.entityType, row.id)}
                                    selected={id === activeOptionId}
                                    onHover={() => setHoveredId(id)}
                                    onSelect={select}
                                />
                            }
                        />
                    ))}
                </GlobalSearchSection>
            ));
        }
        const results = (
            <>
                {bestBet && (
                    <RowWithPeek
                        entityType={bestBet.row.entityType}
                        recordId={bestBet.row.id}
                        surface={surface}
                        hovered={bestBet.id === hoveredId}
                        {...PEEK_PLACEMENT[variant]}
                        row={
                            <GlobalSearchBestBetRow
                                id={bestBet.id}
                                icon={GLOBAL_SEARCH_ENTITY_ICON[bestBet.row.entityType]}
                                entityType={bestBet.row.entityType}
                                title={bestBet.row.displayName}
                                draft={bestBet.row.isDraft}
                                secondary={<HighlightedText text={bestBet.row.secondaryLine} />}
                                href={pathForResult(bestBet.row.entityType, bestBet.row.id)}
                                selected={bestBet.id === activeOptionId}
                                onHover={() => setHoveredId(bestBet.id)}
                                onSelect={bestBet.select}
                            />
                        }
                    />
                )}
                {pageSection}
                {records}
            </>
        );
        body = (
            <div
                id={listboxId}
                role="listbox"
                aria-label={intl.formatMessage({ id: 'global-search.results-label' })}
                className={cn(
                    extras ? 'flex flex-col' : 'overflow-y-auto pb-1',
                    // The sheet is the viewport, so the list takes whatever the field and the
                    // footer leave it; under the box it is bounded by the panel's own height.
                    sheet ? 'min-h-0 flex-1' : 'max-h-[min(70dvh,520px)]'
                )}
            >
                {extras ? (
                    <>
                        <div className="min-h-0 overflow-y-auto pb-1">{results}</div>
                        {extras}
                    </>
                ) : (
                    results
                )}
            </div>
        );
    }

    const correction = onDidYouMean !== undefined && data?.didYouMean ? data.didYouMean : null;

    // What a search ended in, for assistive tech: why nothing is listed, or how many rows are. Nothing while
    // it runs, nor over what was opened recently, which is no answer to a query.
    let outcome = '';
    if (!showRecents && !showSkeleton) {
        if (isError) {
            outcome = errorText;
        } else if (hasOptions) {
            outcome = intl.formatMessage({ id: 'global-search.results-count' }, { count: options.length });
        } else {
            outcome = nothingFoundText;
        }
    }

    return (
        <div
            className={cn(
                'bg-paper overflow-hidden',
                // In the sheet the panel is the sheet's body: the list stretches and the footer sits
                // on the bottom edge of the screen, so neither a shadow nor a corner applies.
                sheet ? 'flex min-h-0 flex-1 flex-col' : 'shadow-dropdown rounded-[10px]'
            )}
        >
            {correction !== null && (
                <button
                    type="button"
                    onClick={() => onDidYouMean?.(correction.term)}
                    className="text-grey-500 hover:bg-grey-100 flex min-h-11 w-full items-center px-3.5 text-left text-xs leading-[1.5]"
                >
                    <FormattedMessage
                        id="global-search.did-you-mean"
                        values={{
                            term: (
                                <span key="term" className="text-link underline">
                                    {correction.term}
                                </span>
                            ),
                        }}
                    />
                </button>
            )}
            {body}
            {/* Outside the listbox, and mounted before its text: a live region that arrives with its words is
                not reliably read. */}
            <div role="status" className="sr-only">
                {outcome}
            </div>
            {showRecents ? (
                <div
                    className={cn(
                        'border-divider bg-grey-50 text-grey-500 flex items-center justify-between gap-3 border-t px-3.5 py-2 text-xs leading-[1.5]',
                        sheet && 'mt-auto'
                    )}
                >
                    <span className="truncate">{intl.formatMessage({ id: 'global-search.keyboard-hint' })}</span>
                    <span className="flex-none">{minLengthHint}</span>
                </div>
            ) : (
                // The closing row counts the records, so it waits for an answer and is not drawn
                // when none came.
                !showSkeleton &&
                !isError &&
                hasOptions &&
                (footer ?? (
                    <a
                        href={searchResultsPath(trimmed)}
                        onClick={(event) => {
                            if (!isPlainLeftClick(event)) {
                                return;
                            }
                            event.preventDefault();
                            model.openResultsPage();
                        }}
                        className={cn(
                            'border-divider bg-primary-surface text-primary flex items-center gap-3 border-t font-semibold no-underline',
                            sheet
                                ? 'mt-auto min-h-[52px] justify-center px-4 text-sm leading-[1.5715]'
                                : 'justify-between px-3.5 py-2.5 text-xs leading-[1.5]'
                        )}
                    >
                        <span className="truncate">
                            {sheet ? (
                                intl.formatMessage(
                                    { id: 'global-search.see-all-total' },
                                    { total: data?.capped ? `${data.total}+` : (data?.total ?? 0) }
                                )
                            ) : (
                                <FormattedMessage
                                    id="global-search.see-all-results"
                                    values={{
                                        total: data?.capped ? `${data.total}+` : (data?.total ?? 0),
                                        // The term reads back as a chip rather than as part of the
                                        // sentence, in the key cap's own box: it is what was typed,
                                        // not wording of ours.
                                        query: (
                                            <span
                                                key="query"
                                                className="border-primary-subtle bg-paper text-primary text-caption rounded-[4px] border px-[5px] py-px font-mono font-medium"
                                            >
                                                {trimmed}
                                            </span>
                                        ),
                                    }}
                                />
                            )}
                        </span>
                    </a>
                ))
            )}
        </div>
    );
}

function Message({ text }: { text: string }) {
    return <p className="text-grey-500 px-3.5 py-6 text-center text-xs leading-[1.5]">{text}</p>;
}
